import { Route, Routes } from '@angular/router';
import { clientCodeGuard } from '@guards/client-code.guard';
import { dashboardInitializationGuard } from '@guards/dashboard-initialization.guard';
import { hasDepartmentAccessGuard } from '@guards/has-department-access.guard';
import { hydrateActiveClientGuard } from '@guards/hydrate-active-client.guard';
import { isAuthenticatedGuard } from '@guards/is-authenticated.guard';
import { isFirebaseAuthenticatedGuard } from '@guards/is-firebase-authenticated.guard';
import { isUserDepartmentAdminGuard } from '@guards/is-user-department-admin.guard';
import { isValidUserGuard } from '@guards/is-valid-user.guard';
import { legacyUserRedirectGuard } from '@guards/legacy-user-redirect.guard';
import { legacyDashboardRedirectGuard } from '@guards/legacy-dashboard-redirect.guard';
import { mustChangePasswordGuard } from '@guards/must-change-password.guard';
import { redirectToPatientGuard } from '@guards/redirect-to-patient.guard';
import { telemonitoringClientCodeGuard } from '@guards/telemonitoring-client-code.guard';
import { FirebaseAuthenticationService } from '@modules/chat/firebase-authentication.service';
import { HomeLayoutComponent } from '@modules/dashboard/home-layout/home-layout.component';
import { HomeComponent } from '@modules/dashboard/home/home.component';
import { LargeLayoutComponent } from '@modules/dashboard/large-layout/large-layout.component';
import { MainLayoutComponent } from '@modules/dashboard/main-layout/main-layout.component';
import { MediumLayoutComponent } from '@modules/dashboard/medium-layout/medium-layout.component';
import { PatientAnalysesComponent } from '@modules/dashboard/patient-analyses/patient-analyses.component';
import { PatientAttachmentsComponent } from '@modules/dashboard/patient-attachments/patient-attachments.component';
import { PatientSequencesComponent } from '@modules/dashboard/patient-sequences/patient-sequences.component';
import { PatientTelemonitoringsComponent } from '@modules/dashboard/patient-telemonitorings/patient-telemonitorings.component';
import { PatientComponent } from '@modules/dashboard/patient/patient.component';
import { PatientsComponent } from '@modules/dashboard/patients/patients.component';
import { SequencesComponent } from '@modules/dashboard/sequences/sequences.component';
import { DepartmentAdminComponent } from '@modules/department/components/department-admin/department-admin.component';
import { DepartmentLabelsAdminComponent } from '@modules/department/components/department-labels-admin/department-labels-admin.component';
import { DepartmentSettingsAdminComponent } from '@modules/department/components/department-settings-admin/department-settings-admin.component';
import { DepartmentUsersAdminComponent } from '@modules/department/components/department-users-admin/department-users-admin.component';
import { DepartmentAdministrationTabs } from '@modules/department/models/department-administration-tabs.enum';
import { DrawerInvokerComponent } from '@modules/drawer/drawer-invoker/drawer-invoker.component';
import { DisabledAccountComponent } from '@modules/shared/disabled-account/disabled-account.component';
import { ServerUnavailableComponent } from '@modules/shared/server-unavailable/server-unavailable.component';
import { AuthenticationErrorComponent } from '@shared/authentication-error/authentication-error.component';
import { PatientTab } from '@shared/models/enums/patient-tabs.enum';

const drawer: Route = {
  path: '',
  component: DrawerInvokerComponent,
  outlet: 'drawer',
};

const patientRoutes: Route = {
  path: 'patients',
  children: [
    {
      path: '',
      component: PatientsComponent,
      canActivate: [redirectToPatientGuard],
    },
    {
      path: ':patientId',
      children: [
        {
          path: '',
          component: PatientComponent,
          children: [
            { path: '', pathMatch: 'full', redirectTo: PatientTab.SEQUENCES },
            {
              path: PatientTab.SEQUENCES,
              component: PatientSequencesComponent,
            },
            {
              path: PatientTab.ANALYSES,
              component: PatientAnalysesComponent,
            },
            {
              path: PatientTab.TELEMONITORINGS,
              component: PatientTelemonitoringsComponent,
            },
            {
              path: PatientTab.ATTACHMENTS,
              component: PatientAttachmentsComponent,
            },
          ],
        },
        drawer,
      ],
    },
  ],
};

const sequenceRoutes: Route = {
  path: 'sequences',
  children: [
    {
      path: '',
      component: SequencesComponent,
    },
    drawer,
  ],
};

const departmentRoutes: Route = {
  path: 'department',
  children: [
    {
      path: ':departmentId',
      canActivate: [hasDepartmentAccessGuard],
      children: [
        sequenceRoutes,
        patientRoutes,
        {
          path: 'admin',
          component: DepartmentAdminComponent,
          canActivate: [isUserDepartmentAdminGuard],
          children: [
            {
              path: '',
              pathMatch: 'full',
              redirectTo: DepartmentAdministrationTabs.USERS,
            },
            {
              path: DepartmentAdministrationTabs.USERS,
              component: DepartmentUsersAdminComponent,
            },
            {
              path: DepartmentAdministrationTabs.LABELS,
              component: DepartmentLabelsAdminComponent,
            },
            {
              path: DepartmentAdministrationTabs.SETTINGS,
              component: DepartmentSettingsAdminComponent,
            },
          ],
        },
      ],
    },
  ],
};

const telemonitoringRoutes: Route = {
  path: 'telemonitorings/:tmId',
  canActivate: [telemonitoringClientCodeGuard],
  loadChildren: () =>
    import('./modules/telemonitoring/telemonitoring.routes').then((m) => m.telemonitoringDetailRoutes),
};

const sadmRoute: Route = {
  path: 'sadms',
  loadChildren: () => import('./modules/sadm/sadm.routes').then((m) => m.sadmRoutes),
};

// sadms are consulted in a large layout, so they can't nest under departmentRoutes (a medium-layout
// child) the way sequences/patients do — this mirrors that path segment for the large layout instead.
const departmentSadmRoutes: Route = {
  path: 'department',
  children: [
    {
      path: ':departmentId',
      children: [sadmRoute],
    },
  ],
};

const clientRoute: Route = {
  path: 'client/:clientCode',
  canActivate: [clientCodeGuard],
  children: [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: 'sequences',
    },
    {
      path: '',
      component: MediumLayoutComponent,
      children: [
        sequenceRoutes,
        patientRoutes,
        {
          path: 'admin',
          loadChildren: () => import('./modules/administration/admin.routes').then((m) => m.adminRoutes),
        },
        departmentRoutes,
        telemonitoringRoutes,
      ],
    },
    {
      path: '',
      component: LargeLayoutComponent,
      children: [sadmRoute, departmentSadmRoutes],
    },
  ],
};

const userRoutes: Route = {
  path: '',
  canActivate: [hydrateActiveClientGuard],
  children: [
    {
      path: '',
      component: HomeLayoutComponent,
      children: [
        {
          path: 'home',
          component: HomeComponent,
        },
      ],
    },
    {
      path: '',
      component: MediumLayoutComponent,
      children: [
        {
          path: 'labels',
          loadChildren: () => import('./modules/labels/labels.routes').then((m) => m.labelsRoutes),
        },
        {
          path: 'profile',
          loadChildren: () => import('./modules/profile/profile.routes').then((m) => m.profileRoutes),
        },
        {
          path: 'exports',
          loadComponent: () => import('./modules/profile/export/export.component').then((m) => m.ExportComponent),
        },
        {
          path: 'invitations',
          loadComponent: () =>
            import('./modules/profile/client-invitations/client-invitations.component').then(
              (m) => m.ClientInvitationsComponent,
            ),
        },
        {
          path: 'sharedtome',
          loadChildren: () => import('./modules/sharedtome/sharedtome.routes').then((m) => m.sharedtomeRoutes),
        },
        {
          path: 'telemonitorings',
          loadChildren: () =>
            import('./modules/telemonitorings/telemonitorings.routes').then((m) => m.telemonitoringsRoutes),
        },
      ],
    },
    {
      path: '',
      component: LargeLayoutComponent,
      children: [
        {
          path: 'chat',
          loadChildren: () => import('./modules/chat/chat.routes').then((m) => m.chatRoutes),
        },
      ],
    },
  ],
};

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/home',
    pathMatch: 'full',
  },
  {
    path: 'authentication-error',
    component: AuthenticationErrorComponent,
  },
  {
    path: 'server-unavailable',
    component: ServerUnavailableComponent,
  },
  {
    path: 'disabled-account',
    component: DisabledAccountComponent,
  },
  {
    path: 'user',
    canActivate: [legacyUserRedirectGuard],
    children: [{ path: '**', children: [] }],
  },
  {
    path: 'dashboard',
    canActivate: [legacyDashboardRedirectGuard],
    children: [{ path: '**', children: [] }],
  },
  {
    path: '',
    component: MainLayoutComponent,
    providers: [FirebaseAuthenticationService],
    canActivate: [isAuthenticatedGuard, isValidUserGuard, mustChangePasswordGuard, isFirebaseAuthenticatedGuard],
    children: [
      {
        path: '',
        canActivate: [dashboardInitializationGuard],
        children: [userRoutes, clientRoute, { path: '**', redirectTo: '/home' }],
      },
    ],
  },
];
